import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Bell,
  PiggyBank,
  FileText,
  Wrench,
  ClipboardList,
  Cpu,
  Bot,
  BarChart3,
  Settings,
  Brain,
  Map
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AltoTechLogo } from '@/components/icons/AltoTechLogo';
import { ProfileMenu } from '@/components/ui/profile-menu';
// import LogoImage from '@/assets/site_logo/brand_logo.png'

// Navigation configuration
export interface NavigationItem {
  name: string;
  href: string;
  icon: () => React.ReactNode;
  description: string;
  group?: string;
  implemented?: boolean;
}

export const navigation: NavigationItem[] = [
  // Core Operations Group
  {
    name: 'Overview',
    href: '/overview',
    icon: () => <Home className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'System overview dashboard',
    group: 'core',
    implemented: true
  },
  {
    name: 'Devices',
    href: '/ahu',
    icon: () => <Cpu className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Manage your connected devices',
    group: 'core',
    implemented: true
  },
  {
    name: 'Autopilot',
    href: '/autopilot',
    icon: () => <Bot className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Automated control system',
    group: 'core',
    implemented: true
  },
  // {
  //   name: 'Copilot',
  //   href: '/copilot',
  //   icon: () => <Brain className='w-5 h-5 text-primary stroke-[1.75px]' />,
  //   description: 'AI assistant for your HVAC system',
  //   group: 'core',
  //   implemented: false // Temporarily disabled
  // },

  // Monitoring & Analytics Group
  {
    name: 'Alarms',
    href: '/alarms',
    icon: () => <Bell className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'System alerts and notifications',
    group: 'monitoring',
    implemented: false
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: () => <BarChart3 className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Performance analytics and trends',
    group: 'monitoring',
    implemented: true
  },
  {
    name: 'Savings',
    href: '/savings',
    icon: () => <PiggyBank className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Energy and cost savings',
    group: 'monitoring',
    implemented: true
  },
  {
    name: 'Map',
    href: '/map',
    icon: () => <Map className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Multi-property energy dashboard',
    group: 'monitoring',
    implemented: true
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: () => <FileText className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Generate and view reports',
    group: 'monitoring',
    implemented: false
  },

  // System Management Group
  {
    name: 'PM',
    href: '/pm',
    icon: () => <Wrench className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'Preventive maintenance',
    group: 'system',
    implemented: false
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: () => <Settings className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'System configuration',
    group: 'system',
    implemented: true
  },
  {
    name: 'Action Log',
    href: '/action-log',
    icon: () => <ClipboardList className='w-5 h-5 text-primary stroke-[1.75px]' />,
    description: 'System activity history',
    group: 'system',
    implemented: false
  },
];

export function Sidebar() {
  const location = useLocation();
  const [showComingSoon, setShowComingSoon] = useState<string | null>(null);
  
  // Get app mode from environment variables
  // const appMode = import.meta.env.VITE_APP_MODE || 'normal';
  const appMode = 'multi';
  
  // Filter navigation items based on mode
  const filteredNavigation = React.useMemo(() => {
    if (appMode === 'multi') {
      // In multi mode, show only Map and Settings tabs
      return navigation.filter(item => item.name === 'Map' || item.name === 'Settings');
    } else {
      // In normal mode, show all tabs except Map
      return navigation.filter(item => item.name !== 'Map');
    }
  }, [appMode]);

  useEffect(() => {
    console.log('filteredNavigation', {appMode, filteredNavigation});
  }, [filteredNavigation]);

  const handleNavigation = (e: React.MouseEvent, item: NavigationItem) => {
    if (!item.implemented) {
      e.preventDefault();
      setShowComingSoon(item.name);

      // Hide the "Coming Soon" message after 2 seconds
      setTimeout(() => {
        setShowComingSoon(null);
      }, 2000);
    }
  };

  return (
    <aside className="w-[3.75rem] h-full bg-white shadow-md flex flex-col relative">
      {/* Logo */}
      {/* Original brand logo - commented for future use */}
      {/* <div className="h-[3.25rem] flex items-center justify-center border-b border-light-grey bg-white/50">
        <img src={LogoImage} alt="Brand Logo" className="h-[1.875rem] p-1" style={{ objectFit: 'contain' }} />
      </div> */}
      
      {/* AltoTech Logo */}
      <div className="h-[3.25rem] flex items-center justify-center border-b border-light-grey bg-white">
        <AltoTechLogo size={40} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 px-1.5 flex flex-col space-y-0.5">
        {filteredNavigation.map((item, index) => {
          const isActive = location.pathname.startsWith(item.href);
          const Icon = item.icon;

          // Add dividers between groups
          const showDivider = index > 0 && (
            (filteredNavigation[index].group === 'monitoring' && filteredNavigation[index - 1].group === 'core') ||
            (filteredNavigation[index].group === 'system' && filteredNavigation[index - 1].group === 'monitoring')
          );

          return (
            <React.Fragment key={item.href}>
              {showDivider && <div className="h-px bg-gray-200 my-1.5 mx-1"></div>}

              <Link
                to={item.implemented ? item.href : '#'}
                onClick={(e) => handleNavigation(e, item)}
                className={cn(
                  "relative flex flex-col items-center justify-center p-1.5 gap-0.5 rounded-lg transition-all duration-200",
                  isActive
                    ? "bg-gradient-to-br from-blue-50 to-blue-100/50 text-primary border border-blue-200/50 shadow-[0_2px_4px_rgba(14,125,228,0.08)]"
                    : "text-gray-400 hover:text-primary hover:bg-blue-50/50"
                )}
              >
                <div className={cn(
                  "p-[0.1875rem] rounded-md transition-all duration-200 relative",
                  isActive
                    ? "bg-white shadow-sm"
                    : "hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/50 hover:shadow-[0_2px_4px_rgba(14,125,228,0.1)]"
                )}>
                  <Icon />
                </div>

                <span className={cn(
                  "text-[9px] mt-[0.1875rem] text-center font-medium transition-all duration-200 leading-none",
                  isActive
                    ? "text-primary"
                    : "text-gray-400 hover:text-primary hover:scale-105"
                )}>
                  {item.name}
                </span>

                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute -right-[2px] top-1/2 -translate-y-1/2 w-1 h-[1.875rem] rounded-l-full bg-primary opacity-50" />
                )}
              </Link>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Profile Menu */}
      <div className="p-[0.3125rem] mt-auto mb-2">
        <ProfileMenu />
      </div>

      {/* Coming Soon Notification */}
      {showComingSoon && (
        <div className="absolute left-[3.75rem] bottom-20 bg-gray-800 text-white px-3 py-2 rounded shadow-lg text-xs animate-fade-in-out whitespace-nowrap">
          {showComingSoon} <span className="font-light">Coming Soon</span>
        </div>
      )}
    </aside>
  );
}